import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { CreditStatus } from '../models/credit-status';
import { Customer } from '../models/customer';
import { CustomerCreditValidation } from '../models/customer-credit-validation';
import { CustomerInfoFromSAP } from '../models/customer-info-from-sap';
import { PreApproveCustomerParams } from '../models/params/preApprove-customer-params';
import { SaveNotEnterCreditRequestReasonParams } from '../models/request/save-no-enter-credit-request-reason-request';
import { ResponseModel } from '../models/response-model';
import { OnbaseProcedureResponseModel } from '../models/response/onbaseProcedureResponseModel';
import { PreApproveCustomerResponseModel } from '../models/response/preApproveCustomerResponseModel';
import { ValidateCreditLimitForInvoicingResponseModel } from '../models/response/validateCreditLimitForInvoicingResponseModel';
import { CreditReferencesResponseModel } from './../models/response/creditReferencesResponseModel';
import { CreditStatusFranchisedResponseModel } from './../models/response/customerCreditStatusResponseModel';
import { ConfigurationService } from './configuration.service';
import { CustomEncoder } from '../@core/encoders/custom-encoder';

@Injectable({
  providedIn: 'root',
})
export class CreditService {
  private url = `${environment.url}/cm`;
  private urlCreditReferences = `${environment.url}/creditReferences`;
  private preApprove:
    | {
      creditPlan: OnbaseProcedureResponseModel | undefined;
      isCreditRequestCreated: boolean;
      preApproveCode: string;
      preApproveAmount: number;
    }
    | undefined;

  constructor(
    private http: HttpClient,
    private configService: ConfigurationService
  ) { }

  getCustomerCreditConditions(
    customerCode: string,
    type: string,
    onbaseProcedureCode?: string,
    salesOrg?: string
  ): Observable<ResponseModel<CustomerCreditValidation>> {
    let params = new HttpParams();

    params = params.set(
      'salesOrg',
      salesOrg ?? this.configService.readSelectedBranchAndSalesOrg().salesOrg.code
    );

    params = params.set('origin', 'retail');

    params = params.set('orderCondition', type);

    if (onbaseProcedureCode) {
      params = params.set('procedureId', onbaseProcedureCode);
    }

    return this.http.get<ResponseModel<CustomerCreditValidation>>(
      `${this.url}/getCustomerCreditConditions/${customerCode}`,
      { params }
    );
  }

  getCustomerCreditStatus(
    customer: Customer,
    branchCode: string,
    sellerCode: string
  ): Observable<CreditStatusFranchisedResponseModel> {
    const params = new HttpParams()
      .append(`fiscalId`, customer.mainPersonalInfo?.fiscalId || '')
      .append(`fiscalIdType`, customer.mainPersonalInfo?.fiscalIdType || '')
      .append(`branchCode`, branchCode)
      .append(`sellerCode`, sellerCode)
      .append(`menu`, `CUSTOMERS`);

    return this.http.post<CreditStatusFranchisedResponseModel>(
      `${this.url}/getCreditStatusFranchised`,
      undefined,
      { params }
    );
  }

  getPreApproveCreditInfo(
    preApproveParams: PreApproveCustomerParams
  ): Observable<PreApproveCustomerResponseModel> {
    const params = new HttpParams({ encoder: new CustomEncoder() })
      .append(`customerCode`, preApproveParams.customerCode)
      .append(`branchCode`, preApproveParams.branchCode)
      .append(`fiscalId`, preApproveParams.fiscalId)
      .append(`fiscalIdType`, preApproveParams.fiscalIdType)
      .append(`limitRule`, preApproveParams.limitRule)
      .append(`menu`, preApproveParams.menu)
      .append(`salesOrg`, preApproveParams.salesOrg)
      .append(`sellerCode`, preApproveParams.sellerCode)
      .append('productCode', preApproveParams.productCode)
      .append(`onbaseProcedureCode`, preApproveParams.onbaseProcedureCode);

    return this.http.post<PreApproveCustomerResponseModel>(
      `${this.url}/preApproveCredit`,
      undefined,
      { params }
    );
  }

  getCustomerCreditStatusWithOutSeller(
    fiscalId: string,
    fiscalIdType: string,
    branchCode: string
  ): Observable<CreditStatus> {
    let params = new HttpParams();
    params = params.set('fiscalId', fiscalId);
    params = params.set('fiscalIdType', fiscalIdType);
    params = params.set('branchCode', branchCode);
    return this.http.post<CreditStatus>(`${this.url}/getCreditStatus`, null, {
      params,
    });
  }

  getCreditReferences(
    salesOrg?: string,
    rateType?: string,
    loanClass?: string
  ): Observable<ResponseModel<Array<CreditReferencesResponseModel>>> {
    let params = new HttpParams();

    if (salesOrg) {
      params = params.append(`salesOrg`, salesOrg);
    }

    if (rateType) {
      params = params.append(`rateType`, rateType);
    }

    if (loanClass) {
      params = params.append(`loanClass`, loanClass);
    }
    return this.http.get<ResponseModel<Array<CreditReferencesResponseModel>>>(
      this.urlCreditReferences,
      {
        params,
      }
    );
  }

  getValidateCreditLimitForInvoicing(
    customerCode: string,
    salesOrg: string,
    fiscalId: string,
    fiscalIdType: string,
    amountTotal: number,
    downPayment: number
  ): Observable<ValidateCreditLimitForInvoicingResponseModel> {
    const params = new HttpParams()
      .append(`customerCode`, customerCode)
      .append(`fiscalId`, fiscalId)
      .append(`fiscalIdType`, fiscalIdType)
      .append(`salesOrg`, salesOrg)
      .append(`amountTotal`, amountTotal.toString())
      .append(`downPayment`, downPayment.toString());

    return this.http.post<ValidateCreditLimitForInvoicingResponseModel>(
      `${this.url}/validateCreditLimitForInvoicing`,
      undefined,
      {
        params,
      }
    );
  }

  validatePastContracts(
    customerCode: string
  ): Observable<ResponseModel<boolean>> {
    const params = new HttpParams().set('customerCode', customerCode);
    return this.http.post<ResponseModel<boolean>>(
      `${this.url}/validateArrears`,
      {},
      {
        params,
      }
    );
  }

  public getPreApprove():
    | {
      creditPlan: OnbaseProcedureResponseModel | undefined;
      isCreditRequestCreated: boolean;
      preApproveCode: string;
      preApproveAmount: number;
    }
    | undefined {
    return this.preApprove;
  }

  public setPreApprove(
    preApprove:
      | {
        creditPlan: OnbaseProcedureResponseModel | undefined;
        isCreditRequestCreated: boolean;
        preApproveCode: string;
        preApproveAmount: number;
      }
      | undefined
  ) {
    this.preApprove = preApprove;
  }

  public saveNoEnterCreditRequestReason({
    customerCode,
    branchCode,
    reasonCode,
    reasonName,
  }: Partial<SaveNotEnterCreditRequestReasonParams>): Observable<
    ResponseModel<boolean>
  > {
    let params = new HttpParams();
    params = customerCode ? params.set('customerCode', customerCode) : params;
    params = branchCode ? params.set('branchCode', branchCode) : params;
    params = reasonCode ? params.set('reasonCode', reasonCode) : params;
    params = reasonName ? params.set('reasonName', reasonName) : params;

    return this.http.post<ResponseModel<boolean>>(
      `${this.url}/reasonNotCreditRequestToLastPreAprove`,
      {},
      { params }
    );
  }

  public saveNoEnterCreditRequestReasonForPreApprove({
    preApproveCode,
    reasonCode,
    reasonName,
  }: Partial<SaveNotEnterCreditRequestReasonParams>): Observable<
    ResponseModel<boolean>
  > {
    let params = new HttpParams();
    params = preApproveCode ? params.set('creditPreAprovedCode', preApproveCode) : params;
    params = reasonCode ? params.set('reasonCode', reasonCode) : params;
    params = reasonName ? params.set('reasonName', reasonName) : params;

    return this.http.post<ResponseModel<boolean>>(
      `${this.url}/reasonNotCreditRequest`,
      {},
      { params }
    );
  }

  public readCustomerFromSAP(customerCode: string, country: string) {
    let params = new HttpParams()
      .set("customerCode", customerCode)
      .set("country", country || "HN");
    return this.http.post<CustomerInfoFromSAP>(`${this.url}/checkBP`, {}, { params });
  }
}
